import { useEffect, useState } from "react";
import { Loader2, Key, Trash2, Save, Eye, EyeOff, Lock } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PROVIDER_LABELS = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI",
  gemini: "Google Gemini",
};

const PROVIDER_HINTS = {
  anthropic: "מתחיל ב־sk-ant-... — מ־https://console.anthropic.com/settings/keys",
  openai: "מתחיל ב־sk-... או sk-proj-... — מ־https://platform.openai.com/api-keys",
  gemini: "מ־Google AI Studio — https://aistudio.google.com/app/apikey",
};

export default function AdminApiKeysTab() {
  const [data, setData] = useState({ keys: [], supported_providers: [] });
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // {provider: "sk-..."}
  const [showInput, setShowInput] = useState({}); // {provider: bool}
  const [saving, setSaving] = useState({}); // {provider: bool}

  const refresh = () => {
    setLoading(true);
    api
      .adminListApiKeys()
      .then(setData)
      .catch(() => setData({ keys: [], supported_providers: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const setDraft = (p, v) => setDrafts((s) => ({ ...s, [p]: v }));

  const save = async (provider) => {
    const key = (drafts[provider] || "").trim();
    if (!key) {
      toast.error("הזן מפתח");
      return;
    }
    setSaving((s) => ({ ...s, [provider]: true }));
    try {
      await api.adminSetApiKey(provider, key);
      toast.success(`מפתח ${PROVIDER_LABELS[provider]} נשמר`);
      setDraft(provider, "");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שמירה נכשלה");
    } finally {
      setSaving((s) => ({ ...s, [provider]: false }));
    }
  };

  const remove = async (provider) => {
    if (!window.confirm(`למחוק את מפתח ${PROVIDER_LABELS[provider]}?`)) return;
    setSaving((s) => ({ ...s, [provider]: true }));
    try {
      await api.adminDeleteApiKey(provider);
      toast.success("המפתח נמחק");
      refresh();
    } catch {
      toast.error("המחיקה נכשלה");
    } finally {
      setSaving((s) => ({ ...s, [provider]: false }));
    }
  };

  if (loading)
    return (
      <div className="grid place-items-center py-20" data-testid="admin-keys-loading">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="admin-api-keys">
      <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-4 text-sm flex gap-3">
        <Lock className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <p className="font-heading text-amber-200/90">מפתחות מרכזיים</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            המפתחות נשמרים מוצפנים (Fernet) במסד הנתונים ומשמשים את כלל המשתמשים. אף משתמש לא יידרש להזין מפתח אישי.
          </p>
        </div>
      </div>

      {data.keys.map((k) => {
        const provider = k.provider;
        const visible = !!showInput[provider];
        const draft = drafts[provider] || "";
        const isSaving = !!saving[provider];
        return (
          <div
            key={provider}
            className="rounded-sm border border-border/60 bg-card/40 p-5"
            data-testid={`admin-api-key-${provider}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-gold" strokeWidth={1.5} />
                <h3 className="font-heading text-base">{PROVIDER_LABELS[provider]}</h3>
              </div>
              {k.is_set ? (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 font-mono"
                  data-testid={`admin-api-key-status-${provider}`}
                >
                  ✓ {k.mask}
                </span>
              ) : (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border/60"
                  data-testid={`admin-api-key-status-${provider}`}
                >
                  לא מוגדר
                </span>
              )}
            </div>
            {k.updated_at && (
              <p className="text-[11px] text-muted-foreground mb-3">
                עודכן: {new Date(k.updated_at).toLocaleString("he-IL")}
              </p>
            )}
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              {PROVIDER_HINTS[provider]}
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[260px] relative">
                <Input
                  type={visible ? "text" : "password"}
                  value={draft}
                  onChange={(e) => setDraft(provider, e.target.value)}
                  placeholder={k.is_set ? "הזן מפתח חדש להחלפה" : "הדבק כאן מפתח..."}
                  data-testid={`admin-api-key-input-${provider}`}
                  className="font-mono text-xs pr-9"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowInput((s) => ({ ...s, [provider]: !visible }))}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={visible ? "הסתר" : "הצג"}
                >
                  {visible ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                </button>
              </div>
              <Button
                onClick={() => save(provider)}
                disabled={isSaving || !draft}
                data-testid={`admin-api-key-save-${provider}`}
                className="sacred-btn"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" strokeWidth={1.5} />שמור</>}
              </Button>
              {k.is_set && (
                <Button
                  variant="outline"
                  onClick={() => remove(provider)}
                  disabled={isSaving}
                  data-testid={`admin-api-key-delete-${provider}`}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
